import { FormEvent, useState } from "react";
import { useResetSkaterPassword } from "../lib/queries";
import { ErrorState } from "./Loading";

/** PRD §11.3: eight characters, the same floor an adult account gets. A 4-digit PIN was explicitly rejected. */
const MIN_PASSWORD_LENGTH = 8;

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
}

/**
 * What a guardian can see and do about their minor's login, once it exists (PRD §11.2).
 *
 * Everything here exists because a minor has no email address, by design rather than by omission (PRD
 * §12, data minimisation). Three consequences follow, and the UI says all three out loud rather than
 * leaving the guardian to work them out:
 *
 * 1. The username is the only handle on the account, and if the guardian forgets it there is nowhere
 *    else to look it up -- so it is shown, always.
 * 2. "Forgot password" cannot reach the child, so a guardian reset is the ONLY way back in. Before this
 *    component existed there was no way back in at all: a forgotten password meant an operator in the
 *    AWS console.
 * 3. Because it is the only route, when it was last used is worth recording and showing.
 */
export function SkaterLoginDetails({
  clientId,
  clientName,
  username,
  createdAt,
  passwordResetAt,
}: {
  clientId: string;
  clientName: string;
  username: string | null;
  createdAt: string | null;
  passwordResetAt: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [resetUsername, setResetUsername] = useState<string | null>(null);
  const resetPassword = useResetSkaterPassword(clientId);

  const created = formatDate(createdAt);
  const lastReset = formatDate(passwordResetAt);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);

    // Checked here as well as on the server: this is a password the guardian types once and then has to
    // relay to a child, so finding out it was too short after a round trip is a worse moment than before.
    if (password.length < MIN_PASSWORD_LENGTH) {
      setLocalError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    // A mistyped password here locks the child out rather than merely annoying the typist -- they aren't
    // present to try it, and there is no self-service route for them to recover through.
    if (password !== confirmPassword) {
      setLocalError("Those two passwords don't match.");
      return;
    }

    resetPassword.mutate(
      { password },
      {
        onSuccess: (result) => {
          setOpen(false);
          setPassword("");
          setConfirmPassword("");
          setResetUsername((result as { username?: string } | undefined)?.username ?? username);
        },
      }
    );
  }

  return (
    <div className="skater-login-details">
      <p className="form-label">{clientName}&rsquo;s login</p>
      <p className="plan-summary">
        Username: <strong>{username ?? "—"}</strong>
        {created && ` · created ${created}`}
        {lastReset && ` · password last reset ${lastReset}`}
      </p>

      {/* Said plainly rather than left implicit. A guardian who doesn't know this will look for a
          "forgot password" link on the child's behalf and find nothing. */}
      <p className="form-hint">
        {clientName} has no email address on file, so they can&rsquo;t use a &ldquo;forgot password&rdquo; link.
        Resetting it here is the only way to get them back into their account.
      </p>

      {resetUsername && !open && (
        <p role="status" className="badge badge-ok">
          Password reset. {clientName} signs in as {resetUsername} with the new password.
        </p>
      )}

      {!open ? (
        <button type="button" className="chip" onClick={() => setOpen(true)}>
          Reset password
        </button>
      ) : (
        <form className="plan-form" onSubmit={handleSubmit}>
          <label htmlFor={`reset-password-${clientId}`}>New password</label>
          <input
            id={`reset-password-${clientId}`}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />

          <label htmlFor={`reset-password-confirm-${clientId}`}>Confirm new password</label>
          <input
            id={`reset-password-confirm-${clientId}`}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />

          {localError && (
            <p role="alert" className="error">
              {localError}
            </p>
          )}
          {resetPassword.isError && <ErrorState error={resetPassword.error} />}

          <button type="submit" disabled={resetPassword.isPending}>
            {resetPassword.isPending ? "Resetting…" : "Set new password"}
          </button>
          <button
            type="button"
            className="chip"
            onClick={() => {
              setOpen(false);
              setLocalError(null);
              setPassword("");
              setConfirmPassword("");
            }}
          >
            Cancel
          </button>
        </form>
      )}
    </div>
  );
}
